import React from "react";
import { Box, Drawer, IconButton, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useIntl } from "react-intl";
import { getAssetHierarchyPalette } from "../assetHierarchyStyles";
import cmmsMessages from "../cmmsMessages";
import AssetNodeDetailsPanel from "./AssetNodeDetailsPanel";

/*
 * The details panel as a drawer, for the table tab.
 *
 * Deliberately thin: it holds a close button and forwards everything else straight through, so
 * the table and the tree show the same panel with the same actions rather than two versions of
 * it that drift. The panel already renders the node's name and its type and status chips, so
 * there is no header to repeat here.
 *
 * The drawer body scrolls rather than the drawer itself — the panel's own tabs (Documents,
 * Machine, Usage) can each be taller than the viewport.
 *
 * Width and surface are the work order drawer's, since the two sit side by side in the module and
 * a person moving between them should not find the panel a different size.
 */
export default function AssetNodeDetailsDrawer({ open, onClose, selectedNode, ...panelProps }) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);

  return (
    <Drawer
      anchor="right"
      // Guarded on the node as well as the flag: deactivating a node clears the selection, and
      // without this the drawer would stay open on the panel's "select a node" placeholder.
      open={Boolean(open && selectedNode)}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 720, lg: 860 }, backgroundColor: cmmsColors.surface } }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1.5, pt: 1.5 }}>
          <IconButton onClick={onClose} aria-label={intl.formatMessage(cmmsMessages.close)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: "auto", px: 2.5, pb: 2.5 }}>
          <AssetNodeDetailsPanel selectedNode={selectedNode} {...panelProps} />
        </Box>
      </Box>
    </Drawer>
  );
}
